<!--
  PlacementSizeEditor -- a saved placement's size, editable in the product
  editor's inspector (Jake, 2026-10-27):

    "the dimensions in the right column should be editable within the
     allowed size for the placement."
    Aspect lock: "always on, shown as a visible lock icon with no checkbox."
    After a location change: "displaying the original dimensions with a
     button to switch back, and with height and width being editable."

  Each size of a design is its own decoration (ADR 0067), so a new size
  REBINDS the placement to that size's decoration (found, or created) --
  PATCH /placements/<id>/ {width_in, height_in} through
  usePlacementSizeChange, which asks first when the store's client has not
  approved the size. Writes land on commit (blur / Enter), like the
  location picker beside it: one write model for the inspector.

  The limits and the shape come from GET /placements/<id>/sizing (the
  location's envelope, the artwork's aspect ratio, and whether the size is
  fixed -- a stitch file is sewn at its own size). The lock is
  useAspectLockedSize: one side drives, the other follows, clamped to the
  envelope.
-->
<template>
    <div
        class="placement-size"
        data-testid="inspector-placement-size"
    >
        <div class="placement-size-row">
            <label class="placement-size-field">
                <span class="placement-size-axis">W</span>
                <input
                    data-testid="inspector-placement-width"
                    class="placement-size-input"
                    type="number"
                    inputmode="decimal"
                    min="0.01"
                    step="0.01"
                    aria-label="Width (inches)"
                    :value="display(size.width.value)"
                    :disabled="inputsDisabled"
                    @input="onInput('width', $event.target.value)"
                    @change="onCommit('width', $event.target.value)"
                >
            </label>
            <span
                v-if="size.locked.value"
                class="material-icons placement-size-lock"
                data-testid="inspector-placement-size-lock"
                role="img"
                aria-label="Proportions locked"
                title="Width and height keep the artwork's proportions"
            >lock</span>
            <span
                v-else-if="shapeUnknown"
                class="material-icons placement-size-lock placement-size-lock--unknown"
                data-testid="inspector-placement-size-shape-unknown"
                role="img"
                aria-label="Shape unknown: proportions not locked"
                title="This artwork's shape is not known, so width and height are not locked together"
            >lock_open</span>
            <span
                v-else
                class="placement-size-times"
                aria-hidden="true"
            >x</span>
            <label class="placement-size-field">
                <span class="placement-size-axis">H</span>
                <input
                    data-testid="inspector-placement-height"
                    class="placement-size-input"
                    type="number"
                    inputmode="decimal"
                    min="0.01"
                    step="0.01"
                    aria-label="Height (inches)"
                    :value="display(size.height.value)"
                    :disabled="inputsDisabled"
                    @input="onInput('height', $event.target.value)"
                    @change="onCommit('height', $event.target.value)"
                >
            </label>
            <span class="placement-size-unit">in</span>
        </div>
        <p
            v-if="check && check.fixed_size"
            class="placement-size-note"
            data-testid="inspector-placement-size-fixed"
        >
            A stitch file is sewn at its own size.
        </p>
        <p
            v-else-if="maxLabel"
            class="placement-size-note"
            data-testid="inspector-placement-size-max"
        >
            Up to {{ maxLabel }}<template v-if="check.location.label">
                at {{ check.location.label }}</template>.
        </p>
        <!-- Card #573: a WARNING for the saved size; never blocks an edit. -->
        <LowResolutionNotice :resolution="check && check.current && check.current.resolution" />
        <!--
            Jake, 2026-10-29 (DTF refuses under 200 since 2026-11-03; this
            box only shows a saved size, which is never edited away): a
            low-resolution DTF image may OPT INTO an Artistic Punch cleanup (button) or, with that
            switched off, is told whom to email.
        -->
        <LowResolutionCleanupAction
            v-if="cleanupResolution"
            :resolution="cleanupResolution"
            :artwork-id="check.current.artwork_id ?? null"
            :width="check.current.width_in"
            :height="check.current.height_in"
        />
        <div
            v-if="previous"
            class="placement-size-previous"
        >
            <span data-testid="inspector-placement-size-previous">
                Was {{ formatSize(previous.width, previous.height) }}<template v-if="previous.label">
                    at {{ previous.label }}</template>.
            </span>
            <button
                type="button"
                class="placement-size-switch-back"
                data-testid="inspector-placement-size-switch-back"
                :disabled="busy"
                @click="switchBack"
            >
                Switch back
            </button>
        </div>
        <p
            v-if="error"
            class="placement-size-error"
            data-testid="inspector-placement-size-error"
            role="alert"
        >
            {{ error }}
        </p>
        <PlacementSizeApprovalDialog
            :model-value="flow.dialog.open"
            :check="flow.dialog.check"
            :is-move="flow.dialog.isMove"
            :busy="flow.dialog.busy"
            :error="flow.dialog.error"
            @keep="flow.keep()"
            @proceed="flow.proceed()"
            @override="flow.override($event)"
            @cancel="flow.cancel()"
        />
    </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import api from "@/services/api";
import { useProductEditorStore } from "@/stores/productEditor";
import { useAspectLockedSize } from "@/composables/useAspectLockedSize";
import LowResolutionNotice from "@/components/decoration/LowResolutionNotice.vue";
import LowResolutionCleanupAction from "@/components/decoration/LowResolutionCleanupAction.vue";
import PlacementSizeApprovalDialog from "./PlacementSizeApprovalDialog.vue";
import {
    forgetPreviousSize,
    formatSize,
    previousSizeFor,
    sizeErrorMessage,
    usePlacementSizeChange,
} from "./usePlacementSizeChange";

const props = defineProps({
    placementId: { type: Number, required: true },
    /** The editor's placement row: its size/decoration/location re-read the limits. */
    placement: { type: Object, default: null },
});

const editor = useProductEditorStore();

const check = ref(null);
const busy = ref(false);
const error = ref("");

const size = useAspectLockedSize({
    aspect: () => check.value?.aspect_ratio ?? null,
    maxWidth: () => check.value?.location?.max_width_in ?? null,
    maxHeight: () => check.value?.location?.max_height_in ?? null,
    fixed: () => Boolean(check.value?.fixed_size),
});

// The shape is not known (an artwork with no stamped or recorded size) and
// the size is not fixed: say the sides are NOT locked rather than show no
// lock at all, which reads as "locked state unknown".
const shapeUnknown = computed(
    () => Boolean(check.value) && !check.value.fixed_size && !size.locked.value,
);

async function refresh() {
    if (typeof editor.loadServerState !== "function") return false;
    try {
        await editor.loadServerState({ force: true, background: true });
    } catch (_err) {
        return false;
    }
    // A new size is a new decoration: re-render like any placement save.
    if (typeof editor.notePlacementSaved === "function") editor.notePlacementSaved();
    return true;
}

const flow = usePlacementSizeChange({ refresh });

// The saved size's verdict when it flags the image (amber or red); only then
// is the optional cleanup worth mounting.
const cleanupResolution = computed(() => {
    const r = check.value?.current?.resolution;
    return r && r.severity ? r : null;
});

const inputsDisabled = computed(() => !check.value || check.value.fixed_size || busy.value);

const maxLabel = computed(() => {
    const loc = check.value?.location;
    if (!loc || loc.max_width_in == null || loc.max_height_in == null) return "";
    return formatSize(loc.max_width_in, loc.max_height_in);
});

const previous = computed(() => {
    const prev = previousSizeFor(props.placementId);
    const cur = check.value?.current;
    if (!prev || !cur) return null;
    if (prev.width === cur.width_in && prev.height === cur.height_in) return null;
    return prev;
});

function display(v) {
    return v == null ? "" : String(v);
}

function showSaved() {
    const cur = check.value?.current;
    size.setSize(cur?.width_in ?? null, cur?.height_in ?? null);
}

async function load() {
    try {
        const resp = await api.get(`/placements/${props.placementId}/sizing`, { params: {} });
        check.value = resp?.data ?? null;
        showSaved();
    } catch (err) {
        check.value = null;
        error.value = sizeErrorMessage(err, "Couldn't load this placement's size.");
    }
}

// Re-read the limits whenever the saved placement changes (a resize, a move,
// a rebind), and on selecting another placement.
watch(
    () => {
        const p = props.placement || {};
        return [
            props.placementId,
            p.decoration_id,
            p.placement_zone_id,
            p.placement_zone_slug,
            p.imprint_width_in,
            p.imprint_height_in,
        ].join("|");
    },
    (key, oldKey) => {
        const sameId = oldKey && oldKey.split("|")[0] === key.split("|")[0];
        if (!sameId) error.value = "";
        load();
    },
    { immediate: true },
);

function parse(raw) {
    if (raw === "" || raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
}

function setSide(side, value, clamp) {
    if (side === "width") size.setWidth(value, { clamp });
    else size.setHeight(value, { clamp });
}

function onInput(side, raw) {
    const n = parse(raw);
    if (n == null) return;
    setSide(side, n, false);
}

async function onCommit(side, raw) {
    const n = parse(raw);
    if (n == null) {
        showSaved();
        return;
    }
    setSide(side, n, true);
    const cur = check.value?.current;
    if (cur && size.width.value === cur.width_in && size.height.value === cur.height_in) return;
    await write({ width: size.width.value, height: size.height.value });
}

async function write(target) {
    busy.value = true;
    error.value = "";
    try {
        const result = await flow.apply({ placementId: props.placementId, size: target });
        if (result.status === "error") {
            error.value = result.error;
            showSaved();
        } else if (result.status === "cancelled") {
            showSaved();
        } else if (result.warning) {
            error.value = result.warning;
        }
        return result;
    } finally {
        busy.value = false;
    }
}

async function switchBack() {
    const prev = previousSizeFor(props.placementId);
    if (!prev) return;
    const result = await write({ width: prev.width, height: prev.height });
    if (result.status === "applied" || result.status === "kept") {
        forgetPreviousSize(props.placementId);
    }
}
</script>

<style scoped>
.placement-size-row {
    display: flex;
    align-items: center;
    gap: 6px;
}
.placement-size-field {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.placement-size-axis,
.placement-size-unit,
.placement-size-times {
    font-size: 12px;
    color: var(--tf-fg-4, #6b7280);
}
.placement-size-input {
    width: 64px;
    font-size: 12px;
    padding: 4px 6px;
    border: 1px solid var(--tf-border-default, #d1d5db);
    border-radius: 4px;
    background: var(--tf-bg-surface, #fff);
}
.placement-size-lock {
    font-size: 16px;
    color: var(--tf-fg-4, #6b7280);
}
.placement-size-lock--unknown {
    opacity: 0.5;
}
.placement-size-note,
.placement-size-previous {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--tf-fg-4, #6b7280);
}
.placement-size-previous {
    display: flex;
    align-items: center;
    gap: 8px;
}
.placement-size-switch-back {
    appearance: none;
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: var(--tf-primary, #2563eb);
    text-decoration: underline;
    cursor: pointer;
}
.placement-size-switch-back:disabled {
    cursor: default;
    opacity: 0.6;
}
.placement-size-error {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--tf-color-danger, #b91c1c);
}
</style>
